/**
 * POS (Plane-Orthogonal-to-Skin) rPPG algorithm.
 *
 * Reference: Wang, W., den Brinker, A. C., Stuijk, S., & de Haan, G. (2017).
 *   "Algorithmic principles of remote PPG."
 *   IEEE Transactions on Biomedical Engineering, 64(7), 1479-1491.
 *
 * The algorithm takes a temporal window of mean-RGB samples from a skin
 * ROI, temporally normalizes them, and projects them onto two axes in a
 * plane orthogonal to the standardized skin-tone direction. The two
 * projected signals are then combined adaptively (alpha-tuned) to produce
 * a single pulse waveform where the blood volume signal is emphasized
 * relative to motion/illumination artifacts.
 *
 * The class processes a sliding window overlap-add style: for every new
 * sample once the buffer is full, we emit one new pulse sample and append
 * it to a rolling output buffer.
 */

export class PosProcessor {
  private readonly windowSize: number;
  private readonly rgbBuffer: Array<[number, number, number]>;
  private readonly pulseBuffer: Float32Array;
  private writeIdx = 0;
  /** Total number of pulse samples ever written (saturates at MAX_SAFE_INTEGER) */
  private totalSamples = 0;

  /**
   * @param windowSize  POS window size in samples. 48 samples at ~30 fps
   *                    covers 1.6 s — a good compromise between suppressing
   *                    low-frequency motion artifacts and responding to
   *                    heart-rate changes.
   * @param bufferSize  Size of the rolling output pulse buffer, in samples.
   *                    Defaults to 300 (~10 s at 30 fps) which is enough for
   *                    FFT-based bpm estimation.
   */
  constructor(windowSize = 48, bufferSize = 300) {
    this.windowSize = windowSize;
    this.rgbBuffer = [];
    this.pulseBuffer = new Float32Array(bufferSize);
  }

  /**
   * Feed a single mean-RGB sample. Returns the latest pulse value once the
   * internal window is filled, or `null` while still warming up.
   */
  push(r: number, g: number, b: number): number | null {
    this.rgbBuffer.push([r, g, b]);
    if (this.rgbBuffer.length < this.windowSize) return null;
    while (this.rgbBuffer.length > this.windowSize) this.rgbBuffer.shift();

    const W = this.windowSize;

    // Window means (temporal normalization divisor)
    let mR = 0, mG = 0, mB = 0;
    for (let i = 0; i < W; i++) {
      mR += this.rgbBuffer[i][0];
      mG += this.rgbBuffer[i][1];
      mB += this.rgbBuffer[i][2];
    }
    mR /= W; mG /= W; mB /= W;
    if (mR <= 0 || mG <= 0 || mB <= 0) return null;

    // Project normalized RGB onto POS plane:
    //   s1 = g/mG - b/mB              (= [0, 1, -1] · rgb_n)
    //   s2 = -2*r/mR + g/mG + b/mB    (= [-2, 1, 1] · rgb_n)
    const s1 = new Float32Array(W);
    const s2 = new Float32Array(W);
    for (let i = 0; i < W; i++) {
      const rn = this.rgbBuffer[i][0] / mR;
      const gn = this.rgbBuffer[i][1] / mG;
      const bn = this.rgbBuffer[i][2] / mB;
      s1[i] = gn - bn;
      s2[i] = -2 * rn + gn + bn;
    }

    // alpha = std(s1) / std(s2) — balances the two projections so the
    // combined signal h = s1 + alpha * s2 has maximal pulse energy.
    const sigma1 = std(s1);
    const sigma2 = std(s2);
    const alpha = sigma2 > 1e-9 ? sigma1 / sigma2 : 1;

    // Combined pulse h (mean-subtracted)
    let hMean = 0;
    const h = new Float32Array(W);
    for (let i = 0; i < W; i++) {
      h[i] = s1[i] + alpha * s2[i];
      hMean += h[i];
    }
    hMean /= W;

    // Overlap-add: use the last sample of the centered window as the
    // newest pulse value. This is a simplified form of the POS overlap-add
    // scheme — we emit one sample per input frame, which is sufficient
    // because downstream FFT operates on the long rolling buffer.
    const pulse = h[W - 1] - hMean;
    const N = this.pulseBuffer.length;
    this.pulseBuffer[this.writeIdx] = pulse;
    this.writeIdx = (this.writeIdx + 1) % N;
    if (this.totalSamples < Number.MAX_SAFE_INTEGER) this.totalSamples++;
    return pulse;
  }

  /**
   * Get the rolling pulse signal in chronological order (oldest first).
   * Returns only as many samples as have been written — fewer than the
   * buffer size during the warm-up period.
   */
  getPulseSignal(): Float32Array {
    const N = this.pulseBuffer.length;
    if (this.totalSamples < N) {
      return this.pulseBuffer.slice(0, this.totalSamples);
    }
    const out = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      out[i] = this.pulseBuffer[(this.writeIdx + i) % N];
    }
    return out;
  }

  /** Reset all internal buffers. Call when the subject changes (new face). */
  reset() {
    this.rgbBuffer.length = 0;
    this.pulseBuffer.fill(0);
    this.writeIdx = 0;
    this.totalSamples = 0;
  }
}

function std(arr: Float32Array): number {
  const n = arr.length;
  if (n === 0) return 0;
  let mean = 0;
  for (let i = 0; i < n; i++) mean += arr[i];
  mean /= n;
  let v = 0;
  for (let i = 0; i < n; i++) {
    const d = arr[i] - mean;
    v += d * d;
  }
  return Math.sqrt(v / n);
}
